import { createContext, useContext, type Provider } from 'react';

/**
 * Constants for the surfaces that have an active experiment controlling the
 * rovo settings modal. Pass via `<RovoSettingsModalProvider source={...}>` so
 * the modal can read the correct experiment gate for that surface.
 */
export const RovoSettingsModalSource = {
	CHAT: 'chat',
	SEARCH: 'search',
} as const;

export type RovoSettingsModalSource =
	(typeof RovoSettingsModalSource)[keyof typeof RovoSettingsModalSource];

export type RovoSettingsConfig = {
	/** Product identifier passed as X-product header to the assistance service (e.g. 'confluence', 'jira') */
	product: string;
	siteId: string;
	experienceId: string;
	assistanceServiceConfig?: {
		baseUrl?: string;
		headers?: Record<string, string>;
	};
	/** Callback invoked when the user clicks "See what Rovo knows about you" in the Work Profile section. */
	onSeeWhatRovoKnows?: () => void;
	/**
	 * The surface that opened the modal (e.g. 'chat', 'search'). Drives experiment
	 * behavior today; will drive analytics attribution per surface (ASIMO-4104).
	 */
	source?: RovoSettingsModalSource | (string & {});
};

const initialState: RovoSettingsConfig = {
	product: '',
	siteId: '',
	experienceId: 'ai-mate',
	assistanceServiceConfig: undefined,
	source: undefined,
};

const rovoSettingsConfig = createContext<RovoSettingsConfig>(initialState);
export const RovoSettingsConfigProvider: Provider<RovoSettingsConfig> = rovoSettingsConfig.Provider;
export const useRovoSettingsConfig = (): RovoSettingsConfig => useContext(rovoSettingsConfig);
